<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-50">
      <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100" leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="flex items-center justify-center min-h-full">
          <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95">
            <DialogPanel data-testid="audit-log-modal" class="w-full h-screen overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl">

              <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <DialogTitle as="h2" class="text-xl font-semibold text-gray-900">
                    {{ $t("event.header.events") }}
                  </DialogTitle>

                </div>
                <button @click="closeModal" data-testid="audit-log-modal-close" class="p-2 text-gray-500 transition-colors rounded-lg hover:text-gray-700 hover:bg-gray-100">
                  <XIcon class="w-6 h-6" />
                </button>
              </div>


              <div class="px-6 py-3 space-y-3 border-b border-gray-200 bg-gray-50">
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <SearchIcon class="w-4 h-4 text-gray-400" />
                  </div>
                  <input v-model="searchQuery" type="text" data-testid="audit-log-modal-search" :placeholder="$t('common.action.search')" class="block w-full py-2 pr-3 text-sm border border-gray-300 rounded-lg pl-9 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>

                <div class="flex flex-wrap items-center gap-2">
                  <button @click="selectedFilter = 'all'" data-testid="audit-log-filter-all" :class="[
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                    selectedFilter === 'all'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  ]">
                    {{ $t('common.filter.all') }}
                  </button>
                  <button @click="selectedFilter = 'error'" data-testid="audit-log-filter-error" :class="[
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                    selectedFilter === 'error'
                      ? 'bg-red-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  ]">
                    {{ $t('common.filter.errors') }}
                  </button>
                  <button @click="selectedFilter = 'warning'" data-testid="audit-log-filter-warning" :class="[
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                    selectedFilter === 'warning'
                      ? 'bg-orange-400 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  ]">
                    {{ $t('common.filter.warnings') }}
                  </button>
                  <button @click="selectedFilter = 'info'" data-testid="audit-log-filter-info" :class="[
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                    selectedFilter === 'info'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  ]">
                    {{ $t('common.filter.info') }}
                  </button>
                  <button @click="selectedFilter = 'user'" data-testid="audit-log-filter-user" :class="[
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                    selectedFilter === 'user'
                      ? 'bg-yellow-400 text-gray-900 shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  ]">
                    {{ $t('common.filter.user') }}
                  </button>
                  <button @click="selectedFilter = 'ok'" data-testid="audit-log-filter-ok" :class="[
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                    selectedFilter === 'ok'
                      ? 'bg-green-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  ]">
                    {{ $t('common.filter.success') }}
                  </button>

                  <div class="w-px h-6 bg-gray-300"></div>

                  <div class="relative">
                    <Listbox v-model="selectedEventTypes" multiple>
                      <ListboxButton class="relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-full hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all">
                        <span class="truncate">{{ $t('common.filter.selectEventTypes') }}</span>
                        <span v-if="selectedEventTypes.length > 0" class="flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-indigo-600 rounded-full">
                          {{ selectedEventTypes.length }}
                        </span>
                        <SelectorIcon class="w-4 h-4 text-gray-400" aria-hidden="true" />
                      </ListboxButton>
                      <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
                        <ListboxOptions class="absolute z-10 py-1 mt-1 overflow-auto text-sm bg-white rounded-lg shadow-lg w-72 max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <ListboxOption v-for="eventType in availableEventTypes" :key="eventType" :value="eventType" v-slot="{ active, selected }" class="cursor-default select-none">
                            <li :class="[
                              active ? 'text-indigo-900 bg-indigo-50' : 'text-gray-900',
                              'relative cursor-pointer select-none py-2 pl-10 pr-4'
                            ]">
                              <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate text-sm']" :title="$t(eventType)">
                                {{ $t(eventType) }}
                              </span>
                              <span v-if="selected" :class="[
                                active ? 'text-indigo-600' : 'text-indigo-600',
                                'absolute inset-y-0 left-0 flex items-center pl-3'
                              ]">
                                <CheckIcon class="w-4 h-4" aria-hidden="true" />
                              </span>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </Listbox>
                  </div>

                  <div class="flex-1"></div>

                  <button @click="toggleExpandCollapseAll" data-testid="audit-log-expand-collapse-all" class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-full hover:border-gray-300 focus:outline-none transition-all" type="button">
                    <component :is="allExpanded ? ChevronUpIcon : ChevronDownIcon" class="w-3 h-3" />
                    <span>{{ allExpanded ? $t('common.action.collapseAll') : $t('common.action.expandAll') }}</span>
                  </button>
                </div>

                <div v-if="selectedEventTypes.length > 0" class="flex flex-wrap items-center gap-1.5 pt-1">
                  <span class="text-xs text-gray-500">{{ $t('common.filter.selected') }}:</span>
                  <div v-for="eventType in selectedEventTypes" :key="eventType" class="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-indigo-700 bg-indigo-100 rounded-full cursor-pointer hover:bg-indigo-200 transition-colors" @click.stop="removeEventType(eventType)">
                    <span class="max-w-[150px] truncate" :title="$t(eventType)">{{ $t(eventType) }}</span>
                    <button @click.stop="removeEventType(eventType)" type="button" class="flex-shrink-0 hover:text-indigo-900 focus:outline-none">
                      <XIcon class="w-3 h-3" />
                    </button>
                  </div>
                  <button @click.stop="clearAllEventTypes" type="button" class="ml-1 text-xs text-gray-500 hover:text-gray-700 focus:outline-none">
                    {{ $t('common.action.clearAll') }}
                  </button>
                </div>
              </div>

              <div class="h-[calc(100vh-200px)] overflow-y-auto px-6 py-4">
                <ul class="space-y-4">
                  <li v-for="event in filteredEvents" :key="event.node.id" data-testid="audit-log-modal-entry">
                    <div class="relative">
                      <div class="relative flex space-x-3">
                        <div>
                          <span :class="[
                            getIconBackground(event.node.type),
                            'h-8 w-8 rounded-full flex items-center justify-center',
                          ]">
                            <component :is="getIcon(event.node.type)" class="w-5 h-5" aria-hidden="true" />
                          </span>
                        </div>
                        <div class="flex-1 w-0">
                          <button @click="toggleEvent(event.node.id)" class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-gray-900 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75">
                            <span>
                              <b v-html="highlightText(dateFormat(event.node.time))"></b>:
                              <span v-html="highlightText($t(event.node.type))"></span>
                            </span>
                            <ChevronUpIcon :class="isEventExpanded(event.node.id) ? 'transform ' : 'rotate-180'" class="w-5 h-5 text-gray-500" />
                          </button>
                          <div v-if="isEventExpanded(event.node.id)" class="px-4 pt-2 text-sm text-gray-500">
                            <highlightjs v-if="!searchQuery.trim()" language="json" :code="formatJson(event.node.data)" />
                            <pre v-else class="overflow-x-auto text-xs" v-html="highlightJson(formatJson(event.node.data))"></pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li v-if="hasMore && !loading" ref="loadMoreTrigger" class="py-4 text-center">
                    <div class="text-sm text-gray-500">{{ $t("common.action.loading") }}</div>
                  </li>
                  <li v-if="loading" class="py-4 text-center">
                    <div class="inline-block w-6 h-6 border-4 border-blue-500 border-solid rounded-full animate-spin border-r-transparent"></div>
                  </li>
                  <li v-if="filteredEvents.length === 0 && !loading" class="py-8 text-center text-gray-500">
                    {{ $t('common.noResults') }}
                  </li>
                </ul>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild, Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import { CheckIcon, ChevronUpIcon, ChevronDownIcon, XIcon, SearchIcon, SelectorIcon } from "@heroicons/vue/solid";
import { InformationCircleIcon, ExclamationIcon, UserIcon } from "@heroicons/vue/outline";
import { EVENT_TYPE_CATEGORY_MAPPING } from "@/lib/alarms";

export default {
  name: "DeviceAuditLogModal",
  components: {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    ChevronUpIcon,
    ChevronDownIcon,
    CheckIcon,
    ExclamationIcon,
    InformationCircleIcon,
    UserIcon,
    XIcon,
    SearchIcon,
    SelectorIcon,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    deviceId: {
      type: String,
      default: null,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const searchQuery = ref('');
    const selectedFilter = ref('all');
    const selectedEventTypes = ref([]);
    const allEvents = ref([]);
    const loading = ref(false);
    const hasMore = ref(false);
    const nextPageToken = ref(null);
    const expandedEvents = ref(new Set());
    const allExpanded = ref(false);
    const loadMoreTrigger = ref(null);

    const closeModal = () => {
      searchQuery.value = '';
      selectedFilter.value = 'all';
      selectedEventTypes.value = [];
      expandedEvents.value = new Set();
      allExpanded.value = false;

      emit('close');
    };

    const getEventCategory = (eventType) => {
      return EVENT_TYPE_CATEGORY_MAPPING[eventType] || 'info';
    };

    const availableEventTypes = computed(() => {
      return Object.keys(EVENT_TYPE_CATEGORY_MAPPING).sort();
    });

    const formatEventType = (eventType) => {
      const parts = eventType.split('::');
      return parts[parts.length - 1].replace(/-/g, ' ').replace(/_/g, ' ');
    };

    const removeEventType = (eventType) => {
      const index = selectedEventTypes.value.indexOf(eventType);
      if (index > -1) {
        selectedEventTypes.value.splice(index, 1);
      }
    };

    const clearAllEventTypes = () => {
      selectedEventTypes.value.splice(0, selectedEventTypes.value.length);
    };

    const toggleExpandCollapseAll = () => {
      allExpanded.value = !allExpanded.value;
      if (allExpanded.value) {
        expandedEvents.value = new Set(filteredEvents.value.map(e => e.node.id));
      } else {
        expandedEvents.value = new Set();
      }
    };

    const isEventExpanded = (eventId) => {
      return expandedEvents.value.has(eventId);
    };

    const toggleEvent = (eventId) => {
      if (expandedEvents.value.has(eventId)) {
        expandedEvents.value.delete(eventId);
      } else {
        expandedEvents.value.add(eventId);
      }
      expandedEvents.value = new Set(expandedEvents.value);
    };

    const hasDataMatch = (event) => {
      if (!searchQuery.value.trim()) return false;
      const query = searchQuery.value.toLowerCase();
      const eventData = event.node.data.toLowerCase();
      return eventData.includes(query);
    };

    const highlightText = (text) => {
      if (!searchQuery.value.trim() || !text) return text;
      const query = searchQuery.value;
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.replace(regex, '<mark class="bg-yellow-300 text-gray-900 rounded px-0.5">$1</mark>');
    };

    const highlightJson = (jsonText) => {
      if (!searchQuery.value.trim() || !jsonText) return jsonText;
      const query = searchQuery.value;
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return jsonText.replace(regex, '<mark class="bg-yellow-300 text-gray-900 rounded px-0.5">$1</mark>');
    };

    const loadEvents = async (reload = false) => {

      if (!props.deviceId) {
        return;
      }

      if (reload) {
        allEvents.value = [];
        nextPageToken.value = null;
        hasMore.value = true;
      }

      if (loading.value) {
        return;
      }

      loading.value = true;

      try {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

        let allLoadedEvents = [];
        let currentPageToken = null;
        let shouldContinue = true;

        while (shouldContinue) {
          const result = await store.dispatch('device/getDeviceEventsExpertMode', {
            deviceId: props.deviceId,
            nextPageToken: currentPageToken,
            take: 100
          });

          const filteredBatch = result.events.filter(event => {
            const eventDate = new Date(event.node.time);
            return eventDate >= threeMonthsAgo;
          });

          allLoadedEvents = [...allLoadedEvents, ...filteredBatch];

          if (!result.hasNextPage) {
            shouldContinue = false;
          } else if (result.events.length > 0) {
            const oldestEventDate = new Date(result.events[result.events.length - 1].node.time);
            if (oldestEventDate < threeMonthsAgo) {
              shouldContinue = false;
            }
          }

          currentPageToken = result.nextPageToken;
        }

        allEvents.value = allLoadedEvents;
        hasMore.value = false;
        nextPageToken.value = null;

        if (allExpanded.value) {
          expandedEvents.value = new Set(allLoadedEvents.map(e => e.node.id));
        }

      } catch (error) {
        console.error('[Modal] Failed to load events:', error);
      } finally {
        loading.value = false;
      }
    };

    const filteredEvents = computed(() => {
      let events = allEvents.value;

      events = events.filter(event =>
        event.node.type in EVENT_TYPE_CATEGORY_MAPPING
      );

      if (selectedEventTypes.value.length > 0) {
        events = events.filter(event =>
          selectedEventTypes.value.includes(event.node.type)
        );
      }

      if (selectedFilter.value !== 'all') {
        events = events.filter(event =>
          getEventCategory(event.node.type) === selectedFilter.value
        );
      }

      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        events = events.filter(event => {
          const eventType = event.node.type.toLowerCase();
          const eventTypeTranslated = t(event.node.type).toLowerCase();
          const eventData = event.node.data.toLowerCase();
          const eventTime = new Date(event.node.time).toLocaleString("de-DE").toLowerCase();

          return eventType.includes(query) ||
            eventTypeTranslated.includes(query) ||
            eventData.includes(query) ||
            eventTime.includes(query);
        });
      }

      return events;
    });

    watch(searchQuery, () => {
      if (searchQuery.value.trim()) {
        filteredEvents.value.forEach(event => {
          if (hasDataMatch(event)) {
            expandedEvents.value.add(event.node.id);
          }
        });

        expandedEvents.value = new Set(expandedEvents.value);
      } else {
        if (!allExpanded.value) {
          expandedEvents.value = new Set();
        }
      }
    });

    watch(() => props.isOpen, async (isOpen) => {
      if (isOpen && props.deviceId) {
        await loadEvents(true);
      }
    });

    const getIconBackground = (event_type) => {
      const event_category = EVENT_TYPE_CATEGORY_MAPPING[event_type];
      switch (event_category) {
        case "error":
          return "bg-red-500 text-white";
        case "info":
          return "bg-blue-500 text-white";
        case "user":
          return "bg-yellow-100 text-gray-600";
        case "ok":
          return "bg-green-500 text-white";
      }
      return "bg-blue-500";
    };

    const getIcon = (event_type) => {
      const event_category = EVENT_TYPE_CATEGORY_MAPPING[event_type];
      switch (event_category) {
        case "error":
          return ExclamationIcon;
        case "info":
          return InformationCircleIcon;
        case "user":
          return UserIcon;
        case "ok":
          return CheckIcon;
      }
      return InformationCircleIcon;
    };

    const dateFormat = (date) => {
      return new Date(date).toLocaleString("de-DE");
    };

    const parseRecursive = (value) => {
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return parseRecursive(parsed);
        } catch {
          return value;
        }
      }

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const result = {};
        for (const [key, val] of Object.entries(value)) {
          result[key] = parseRecursive(val);
        }
        return result;
      }

      if (Array.isArray(value)) {
        return value.map(item => parseRecursive(item));
      }

      return value;
    };

    const formatJson = (data) => {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        const fullyParsed = parseRecursive(parsed);
        return JSON.stringify(fullyParsed, null, 2);
      } catch (e) {
        return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      }
    };

    return {
      searchQuery,
      selectedFilter,
      selectedEventTypes,
      availableEventTypes,
      filteredEvents,
      loading,
      hasMore,
      loadMoreTrigger,
      expandedEvents,
      allExpanded,
      closeModal,
      getEventCategory,
      formatEventType,
      removeEventType,
      clearAllEventTypes,
      toggleExpandCollapseAll,
      isEventExpanded,
      toggleEvent,
      hasDataMatch,
      highlightText,
      highlightJson,
      getIconBackground,
      getIcon,
      dateFormat,
      formatJson,
    };
  },
};
</script>
